// src/components/DirectoryTree/DirectoryTree.jsx
import React, { useEffect, useState, useCallback } from 'react';
import { Box, List, CircularProgress, Alert, Snackbar } from '@mui/material';
import DirectoryNode from '../DirectoryNode/DirectoryNode';
import { getArchiveDirectory, downloadArchiveFile } from '../../services/documentService';
import { buildTreeNode, isVisibleFile } from '../../utilities/treeUtils';
import DocumentViewModal from '../DocumentViewModal';

// visible document extensions for zip extraction
const ZIP_VISIBLE_EXTS = ['.pdf', '.doc', '.docx', '.xls', '.xlsx'];

// Use visible-file helper from treeUtils: only docs, excel and videos allowed
const isVisibleFileByName = (name) => isVisibleFile(name);

const normalizeArchiveRootPath = (path) => {
    if (!path) return null;
    return path.replace(/^\/+|\/+$/g, '');
};

const getEntryName = (entry) => (typeof entry === 'string' ? entry : entry?.name || '');

const findDirectIndexHtml = (data) => {
    const files = Array.isArray(data?.files) ? data.files : [];
    return files.find(file => getEntryName(file).toLowerCase() === 'index.html') || null;
};

const hasStaticPackageShape = (folderPath, data) => {
    const folderName = (folderPath || '').split('/').pop().toLowerCase();
    const directoryNames = new Set(
        (data?.directories || []).map(dir => getEntryName(dir).toLowerCase())
    );
    const fileNames = new Set(
        (data?.files || []).map(file => getEntryName(file).toLowerCase())
    );

    return folderName === 'online' ||
        folderName === 'html' ||
        directoryNames.has('assets') ||
        fileNames.has('project.txt') ||
        fileNames.has('goodbye.html');
};

const DirectoryTree = ({ rootPath = '/', onFileClick: externalFileClick }) => {
    const [directoryData, setDirectoryData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [expandedFolders, setExpandedFolders] = useState({});
    const [loadingChildren, setLoadingChildren] = useState({});
    const [error, setError] = useState(null);

    // Modal state for file preview (used for CBT/HTML files and fallback)
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedFile, setSelectedFile] = useState({ path: null, name: null });
    const archiveRootPath = normalizeArchiveRootPath(rootPath);

    // ─── CBT / Training helpers (mirrors BasicTraining logic) ──────────────────

    /**
     * Returns true when the folder at folderPath is a B-CBT folder
     * (i.e. its name normalises to "bcbt" or "cbt").
     */
    const isCBTFolder = useCallback((folderPath) => {
        const folderName = (folderPath || '').split('/').pop().toLowerCase();
        const normalized = folderName.replace(/\s+/g, '').replace(/-/g, '');
        return normalized === 'bcbt' || normalized === 'cbt' || normalized === 'online';
    }, []);

    /**
     * Fetches directory content from the backend.
     */
    const fetchDirectoryContent = useCallback(async (path) => {
        const normalizedPath = !path || path === '/' ? '' : path.replace(/^\/+|\/+$/g, '');
        const response = await getArchiveDirectory(normalizedPath);
        return response?.data || response || { directories: [], files: [] };
    }, []);

    /**
     * Recursively searches for index.html inside a CBT folder,
     * first looking inside an "html" sub-folder (same as BasicTraining).
     */
    const findIndexHtml = useCallback(async (folderPath) => {
        try {
            const data = await fetchDirectoryContent(folderPath);

            // 1. Look inside an "html" sub-directory first
            if (data.directories) {
                for (const dir of data.directories) {
                    if (dir.name.toLowerCase() === 'html') {
                        const htmlData = await fetchDirectoryContent(dir.path);

                        // Files directly inside /html/
                        if (htmlData.files) {
                            const indexFile = findDirectIndexHtml(htmlData);
                            if (indexFile) return indexFile;
                        }

                        // Sub-directories of /html/
                        if (htmlData.directories) {
                            for (const subDir of htmlData.directories) {
                                const subData = await fetchDirectoryContent(subDir.path);
                                if (subData.files) {
                                    const indexFile = findDirectIndexHtml(subData);
                                    if (indexFile) return indexFile;
                                }
                            }
                        }
                    }
                }
            }

            // 2. Fallback: look directly inside the CBT folder
            if (data.files) {
                const indexFile = findDirectIndexHtml(data);
                if (indexFile) return indexFile;
            }

            return null;
        } catch (err) {
            console.error('DirectoryTree: Error finding index.html:', err);
            return null;
        }
    }, [fetchDirectoryContent]);

    const openHtmlFile = useCallback((htmlFile) => {
        if (!htmlFile?.path) return;

        const fileName = getEntryName(htmlFile) || htmlFile.path.split('/').pop();
        if (externalFileClick) {
            externalFileClick(htmlFile.path, fileName);
        } else {
            setSelectedFile({ path: htmlFile.path, name: fileName });
            setModalOpen(true);
        }
    }, [externalFileClick]);

    // ─── File / folder interaction ──────────────────────────────────────────────

    /**
     * Opens a file. HTML files (including CBT index.html) are shown in the
     * DocumentViewModal; all other files are downloaded/opened in a new tab.
     * If a parent passed `onFileClick`, that handler is also called.
     */
    const handleFileClick = useCallback(async (filePath) => {
        if (!filePath) return;

        const fileName = filePath.split('/').pop();

        if (externalFileClick) {
            // Let the parent (DocumentationSidebar) handle it
            externalFileClick(filePath, fileName);
            return;
        }

        const isHtml = fileName.toLowerCase().endsWith('.html') ||
            fileName.toLowerCase().endsWith('.htm');
        const isZip = fileName.toLowerCase().endsWith('.zip');

        if (isHtml) {
            setSelectedFile({ path: filePath, name: fileName });
            setModalOpen(true);
        } else {
            return;
        }

        // handle zip extraction: show contained PDF/DOC/XLS files
        if (isZip) {
            try {
                const arrayBuffer = await downloadArchiveFile(filePath);
                let JSZip;
                try {
                    JSZip = (await import('jszip')).default || (await import('jszip'));
                } catch (ie) {
                    setError('ZIP preview requires the "jszip" package to be installed on the frontend.');
                    return;
                }
                const zip = await JSZip.loadAsync(arrayBuffer);
                const entries = Object.keys(zip.files)
                    .filter(n => ZIP_VISIBLE_EXTS.some(ext => n.toLowerCase().endsWith(ext)))
                    .map(n => ({ name: n, path: `${filePath}::${n}` }));
                if (entries.length === 0) {
                    setError('No previewable documents found inside ZIP.');
                    return;
                }
                // extract and open the first PDF/DOC/XLS found
                const entryName = entries[0].name;
                const fileBlob = await zip.file(entryName).async('blob');
                const url = URL.createObjectURL(fileBlob);
                window.open(url, '_blank');
                setTimeout(() => URL.revokeObjectURL(url), 10000);
                return;
            } catch (err) {
                console.error('Failed to preview zip:', err);
                setError(`Failed to preview ZIP: ${err.message}`);
                return;
            }
        }
        try {
            const response = await downloadArchiveFile(filePath);
            const blob = new Blob([response], { type: 'application/octet-stream' });
            const url = URL.createObjectURL(blob);
            window.open(url, '_blank');
            setTimeout(() => URL.revokeObjectURL(url), 10000);
        } catch (err) {
            console.error('DirectoryTree: Error downloading file:', err);
            setError(`Failed to open file: ${err.message}`);
        }
    }, [externalFileClick]);

    const handleFolderToggle = useCallback(async (folderPath) => {
        // ── CBT folder: find and open index.html directly ───────────────────
        if (isCBTFolder(folderPath)) {
            setLoadingChildren(prev => ({ ...prev, [folderPath]: true }));
            try {
                const indexFile = await findIndexHtml(folderPath);
                if (indexFile?.path) {
                    openHtmlFile(indexFile);
                } else {
                    setError('index.html not found in this training module.');
                }
            } catch (err) {
                setError(`Error opening training module: ${err.message}`);
            } finally {
                setLoadingChildren(prev => ({ ...prev, [folderPath]: false }));
            }
            return;
        }

        // ── Normal folder toggle ─────────────────────────────────────────────
        const isExpanded = expandedFolders[folderPath];

        if (isExpanded) {
            // Collapse this branch and clear stale expanded/loading state for descendants.
            setExpandedFolders(prev => {
                const next = { ...prev };
                Object.keys(next).forEach(path => {
                    if (path === folderPath || path.startsWith(`${folderPath}/`)) {
                        delete next[path];
                    }
                });
                return next;
            });

            setLoadingChildren(prev => {
                const next = { ...prev };
                Object.keys(next).forEach(path => {
                    if (path === folderPath || path.startsWith(`${folderPath}/`)) {
                        delete next[path];
                    }
                });
                return next;
            });
            return;
        }

        setLoadingChildren(prev => ({ ...prev, [folderPath]: true }));
        try {
            const data = await fetchDirectoryContent(folderPath);
            const directIndexFile = findDirectIndexHtml(data);

            if (directIndexFile?.path && hasStaticPackageShape(folderPath, data)) {
                openHtmlFile(directIndexFile);
                return;
            }

            const folderNode = buildTreeNode(data, folderPath);

            // Prune children: remove empty folders and filtered files
            const childChecks = (folderNode.children || []).map(async (child) => {
                if (child.type === 'file') {
                    // keep file only if it's a visible type
                    if (!isVisibleFileByName(child.name)) return null;
                    return child;
                }

                if (child.type === 'folder') {
                    // fetch child content to determine if it contains any visible files or subdirectories
                    try {
                        const childData = await fetchDirectoryContent(child.path);
                        const childIndexFile = findDirectIndexHtml(childData);
                        if (childIndexFile?.path && hasStaticPackageShape(child.path, childData)) {
                            return child;
                        }

                        const visibleFiles = (childData.files || []).filter(f => isVisibleFileByName(typeof f === 'string' ? f : f.name));
                        const hasDirs = Array.isArray(childData.directories) && childData.directories.length > 0;
                        if (visibleFiles.length === 0 && !hasDirs) {
                            return null; // prune empty folder
                        }
                        return child;
                    } catch (err) {
                        // If fetch fails, keep the folder (fail-open)
                        return child;
                    }
                }
                return child;
            });

            const checked = await Promise.all(childChecks);
            const filteredChildren = checked.filter(Boolean);

            setDirectoryData(prev => {
                const patch = (nodes = []) =>
                    nodes.map(n => {
                        if (n.path === folderPath)
                            return { ...n, children: filteredChildren };
                        if (Array.isArray(n.children))
                            return { ...n, children: patch(n.children) };
                        return n;
                    });
                return patch(prev);
            });
        } catch (err) {
            setError(`Failed to load folder: ${err.message}`);
        } finally {
            setLoadingChildren(prev => ({ ...prev, [folderPath]: false }));
        }

        setExpandedFolders(prev => ({ ...prev, [folderPath]: true }));
    }, [expandedFolders, isCBTFolder, findIndexHtml, fetchDirectoryContent, openHtmlFile]);

    // ─── Load root folder on mount / rootPath change ────────────────────────────
    useEffect(() => {
        const loadRoot = async () => {
            setLoading(true);
            try {
                const data = await fetchDirectoryContent(rootPath);
                const rootNode = buildTreeNode(data, rootPath);
                setDirectoryData([rootNode]);
                setExpandedFolders({ [rootPath]: true });
            } catch (err) {
                setError(`Failed to load content: ${err.message}`);
            } finally {
                setLoading(false);
            }
        };

        loadRoot();
    }, [rootPath, fetchDirectoryContent]);

    // ─── Render ──────────────────────────────────────────────────────────────────

    if (loading) {
        return (
            <Box sx={{ p: 4, textAlign: 'center' }}>
                <CircularProgress size={24} />
            </Box>
        );
    }

    return (
        <>
            {error && (
                <Snackbar
                    open={!!error}
                    autoHideDuration={6000}
                    onClose={() => setError(null)}
                    anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                >
                    <Alert severity="error" onClose={() => setError(null)}>
                        {error}
                    </Alert>
                </Snackbar>
            )}

            <List dense disablePadding>
                {directoryData.flatMap(root =>
                    (root.children || []).map(child => (
                        <DirectoryNode
                            key={child.path}
                            node={child}
                            onFileClick={handleFileClick}
                            expandedFolders={expandedFolders}
                            onFolderToggle={handleFolderToggle}
                            loadingChildren={loadingChildren}
                            isCBTFolder={isCBTFolder}
                        />
                    ))
                )}
            </List>

            {/* Modal for HTML / CBT files when no external handler is provided */}
            <DocumentViewModal
                open={modalOpen}
                onClose={() => {
                    setModalOpen(false);
                    setSelectedFile({ path: null, name: null });
                }}
                filePath={selectedFile.path}
                fileName={selectedFile.name}
                archiveRootPath={archiveRootPath}
            />
        </>
    );
};

export default DirectoryTree;
